import { DOCUMENT } from '@angular/common';
import { InjectionToken, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { LogLevel, OpenIdConfiguration } from 'angular-auth-oidc-client';
import { first } from 'rxjs';

import { ON_BEHALF_OF_QUERY_PARAM } from '@mobi/rwc-utils-azure-ng-jslib';
import {
    CIP_OPENID_ENDPOINT,
    ConfigurationService,
    InfraPreset,
    LanguageService,
    Stage
} from '@mobi/rwc-utils-common-ng-jslib';

import { OAuthConfig, POST_LOGIN_URL } from '../authentication-config.provider';

export const CIP_CONFIG_ID = 'CIP';

const DEFAULT_SCOPES = ['email', 'profile', 'openid'];

export const CUSTOM_CIP_CONFIG = new InjectionToken<OpenIdConfiguration>(
    'Custom CIP configuration. To override default CIP configuration.'
);

export class CipAuthConfig implements OAuthConfig {
    #customConfig = inject(CUSTOM_CIP_CONFIG, { optional: true }) ?? {};
    #configurationService = inject(ConfigurationService);
    #language = toSignal(inject(LanguageService).language$.pipe(first()), { requireSync: true })();
    #document = inject(DOCUMENT);

    #location = this.#document.location;
    #localStorage = this.#document.defaultView?.localStorage;

    loadConfig(): OpenIdConfiguration {
        const stage = this.#configurationService.getSync('stage') as Stage;
        const infraPreset = this.#configurationService.getSync('infraPreset') as InfraPreset;
        const scope = DEFAULT_SCOPES.join(' ');
        const clientId = this.#configurationService.getTkNameId();
        const logLevel = LogLevel.Error;
        const authority = this.#getAuthorityDomain(stage) + CIP_OPENID_ENDPOINT;
        const customParamsAuthRequest: Record<string, string> = Object.fromEntries(
            Object.entries({
                ...getIdpHintParam(stage, infraPreset, this.#location),
                login_hint: this.#localStorage?.getItem('cip-login-hint'),
                language: this.#language
            }).filter((entry): entry is [string, string] => Boolean(entry[1]))
        );

        return {
            authority,
            clientId,
            customParamsAuthRequest,
            logLevel,
            scope,
            autoUserInfo: false,
            configId: CIP_CONFIG_ID,
            disableIatOffsetValidation: true,
            ignoreNonceAfterRefresh: true,
            postLoginRoute: sessionStorage.getItem(POST_LOGIN_URL) ?? undefined,
            postLogoutRedirectUri: window.location.origin,
            redirectUrl: `${window.location.origin}/login-callback`,
            renewTimeBeforeTokenExpiresInSeconds: 10,
            renewUserInfoAfterTokenRenew: true,
            responseType: 'code',
            silentRenew: true,
            startCheckSession: false,
            triggerAuthorizationResultEvent: false,
            useRefreshToken: true,
            ...this.#customConfig
        };
    }

    #getAuthorityDomain(stage: Stage): string {
        const urlSearchParams = new URLSearchParams(this.#location.search);
        const customIdpAuthority = urlSearchParams.get('customIdpAuthority') as string | null;
        if (customIdpAuthority) {
            return customIdpAuthority;
        }
        switch (stage) {
            case 'prod':
                return 'https://idp.cip.mobiliar.ch';
            case 'local-dev':
            case 'preprod':
                return 'https://idp.cip.mobiliar-preprod.ch';
            case 'sit':
                return 'https://idp.cip.mobiliar-sit.ch';
        }
    }
}

/**
 * Business logic:
 * * if no loginIdp search param is provided, default
 * * if loginIdp search param is provided but empty, do not set any hint to select idp in login-screen of keycloak
 * * if loginIdp search param is provided with a value, use that value as hint
 */
export function getIdpHintParam(stage: Stage, infraPreset: InfraPreset, location: Location): IdpHint | undefined {
    const urlSearchParams = new URLSearchParams(location.search);
    const loginIdp = urlSearchParams.get('loginIdp') as IdpHint['kc_idp_hint'] | null | '';
    const onBehalfOf = urlSearchParams.get(ON_BEHALF_OF_QUERY_PARAM);
    if (loginIdp === null && onBehalfOf === null) {
        return getDefaultIdpHint(stage, infraPreset);
    }
    if (loginIdp === '') {
        return undefined;
    }
    return { kc_idp_hint: loginIdp ?? 'entra-mobiliar-prod' };
}

function getDefaultIdpHint(stage: Stage, infraPreset: InfraPreset): IdpHint | undefined {
    switch (infraPreset) {
        case 'b2b':
            return getB2bDefaultIdpHint(stage);
        case 'b2c':
            return getB2cDefaultIdpHint(stage);
        case 'b2eadmin':
        case 'b2e':
            return { kc_idp_hint: 'entra-mobiliar-prod' };
        default:
            return undefined;
    }
}

function getB2bDefaultIdpHint(stage: Stage): IdpHint | undefined {
    switch (stage) {
        case 'local-dev':
            return undefined;
        case 'prod':
            return { kc_idp_hint: 'wesb2b-prod' };
        default:
            return { kc_idp_hint: 'wesb2b-int' };
    }
}

function getB2cDefaultIdpHint(stage: Stage): IdpHint | undefined {
    if (stage === 'prod') {
        return { kc_idp_hint: 'wesb2c-prod' };
    } else {
        return { kc_idp_hint: 'wesb2c-int' };
    }
}

type IdpHint = Record<
    'kc_idp_hint',
    'wesb2b-prod' | 'wesb2b-int' | 'wesb2c-prod' | 'wesb2c-int' | 'entra-mobiliar-prod'
>;
